import React, { useState, useMemo } from 'react';
import { useCustomers } from '../hooks/useCustomers';
import { useOrganizations } from '../hooks/useOrganizations';
import CustomerForm from '../components/customers/CustomerForm';
import { Customer, CreateCustomerData } from '../types/customer';
import { Organization } from '../types/organization';
import {
  PlusIcon,
  EnvelopeIcon,
  PhoneIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  BuildingOffice2Icon,
  UserIcon,
  InformationCircleIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  DocumentIcon,
  DocumentTextIcon,
  PaperClipIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentCheckIcon,
  DocumentChartBarIcon,
  EyeIcon,
  UsersIcon,
  LinkIcon
} from '@heroicons/react/24/outline';
import { Tooltip } from '../components/common/Tooltip';
import SearchBar from '../components/common/SearchBar';
import LoadingScreen from '../components/common/LoadingScreen';
import toast from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';
import Modal from '../components/common/Modal';
import ImportWizard from '../components/common/ImportWizard';
import ExportButton from '../components/common/ExportButton';
import { customerFieldMappings } from '../utils/fieldMappings';
import { getImportExportLang } from '../translations/imp_lang';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { formatDocumentDate } from 'utils/formatters';
import Pagination from 'components/common/Pagination';
import ConfirmDeleteModal from 'components/common/ConfirmDeleteModal';
import CustomerViewModal from 'components/customers/CustomerViewModal';
import CustomerList from 'components/customers/CustomerList';
import { customerToKeyValuePairs } from '../utils/customerDataMapper';
import DocumentViewerModal from 'components/documents/DocumentViewerModal';
import DocumentGeneratorModal from 'components/documents/DocumentGeneratorModal';
import CommonPageHeader from '../components/common/CommonPageHeader';
import CustomerRegistrationLinkGenerator from '../components/customers/CustomerRegistrationLinkGenerator';
import GroupAssignmentModal from '../components/customers/GroupAssignmentModal';
import { useCustomerGroups } from '../hooks/useCustomerGroups';
import SendInvitationModal from '../components/customers/SendInvitationModal';

export interface SimpleOrganization {
  id: string;
  name: string;
}

const Customers: React.FC = () => {
  const { t, language } = useLanguage();
  const tr = getImportExportLang(language);
  const { customers, loading, createCustomer, updateCustomer, deleteCustomer, bulkDeleteCustomers, refresh } = useCustomers();
  const { organizations } = useOrganizations();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedOrgs, setSelectedOrgs] = useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const { groups: customerGroups } = useCustomerGroups();
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [isDocumentGeneratorOpen, setIsDocumentGeneratorOpen] = useState(false);
  const [customerWithDocuments, setCustomerWithDocuments] = useState<Customer | null>(null);
  const [customerForDocGen, setCustomerForDocGen] = useState<Customer | null>(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isRegistrationLinkOpen, setIsRegistrationLinkOpen] = useState(false);
  const [isSendInvitationOpen, setIsSendInvitationOpen] = useState(false);
  const [customerForInvitation, setCustomerForInvitation] = useState<Customer | null>(null);

  // Bulk select state
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const [showGroupAssignment, setShowGroupAssignment] = useState(false);
  const [isConfirmBulkDeleteOpen, setIsConfirmBulkDeleteOpen] = useState(false);
  const [bulkOrgId, setBulkOrgId] = useState('');
  const [isBulkAssigning, setIsBulkAssigning] = useState(false);
  const [isImportWizardOpen, setIsImportWizardOpen] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const handleCreateCustomer = async (data: CreateCustomerData) => {
    try {
      await createCustomer(data);
      setIsModalOpen(false);
      toast.success(t('common.messages.customer.createdSuccess'));
    } catch (error) {
      console.error('Error creating customer:', error);
      toast.error(t('common.messages.customer.createdError'));
    }
  };

  const handleEditCustomer = async (data: CreateCustomerData) => {
    if (!selectedCustomer) return;

    try {
      await updateCustomer(selectedCustomer.id, data);
      setIsModalOpen(false);
      setSelectedCustomer(null);
      toast.success(t('common.messages.customer.updatedSuccess'));
    } catch (error) {
      console.error('Error updating customer:', error);
      toast.error(t('common.messages.customer.updatedError'));
    }
  };

  const handleDeleteCustomer = async (customer: Customer) => {
    try {
      await deleteCustomer(customer.id);
      toast.success(t('common.messages.customer.deletedSuccess'));
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error(t('common.messages.customer.deletedError'));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCustomerIds.length === 0) return;

    try {
      const result = await bulkDeleteCustomers(selectedCustomerIds);
      
      if (result.failed === 0) {
        toast.success(t('customers.bulkActions.deleteSuccess', { count: result.success }));
      } else if (result.success > 0) {
        toast.success(t('customers.bulkActions.deletePartialSuccess', { 
          success: result.success, 
          failed: result.failed 
        }));
      } else {
        toast.error(t('customers.bulkActions.deleteFailed'));
      }
      
      setSelectedCustomerIds([]);
      setIsConfirmBulkDeleteOpen(false);
    } catch (error) {
      console.error('Error bulk deleting customers:', error);
      toast.error(t('customers.bulkActions.deleteFailed'));
    }
  };

  const handleStatusChange = async (customer: Customer) => {
    try {
      const newStatus = customer.status === 'active' ? 'inactive' : 'active';
      await updateCustomer(customer.id, {
        ...customer,
        status: newStatus,
        isActive: newStatus === 'active'
      });
      toast.success(`${t('common.messages.customer.customerStatusUpdated')} ${customer.status === 'active' ? 'deactivated' : 'activated'}`);
    } catch (error) {
      console.error('Error updating customer status:', error);
      toast.error(t('common.messages.customer.customerStatusUpdateFailed'));
    }
  };

  const openEditModal = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsModalOpen(true);
  };

  // Function to open the document viewer modal
  const openDocumentModal = (customer: Customer) => {
    if (customer.documents && customer.documents.length > 0) {
      setCustomerWithDocuments(customer);
      setIsDocumentModalOpen(true);
    } else {
      toast.error(t('customers.messages.noDocuments') || 'No documents available');
    }
  };

  const openViewModal = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsViewModalOpen(true);
  };

  const handleGenerateDocument = (customer: Customer) => {
    setCustomerForDocGen(customer);
    setIsDocumentGeneratorOpen(true);
  };

  const handleSendInvitation = async (customer: Customer) => {
    // Refresh customer data to get latest contactPersons
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../lib/firebase');
      
      const customerDoc = await getDoc(doc(db, 'customers', customer.id));
      if (customerDoc.exists()) {
        const freshCustomer = {
          id: customerDoc.id,
          ...customerDoc.data(),
          createdAt: customerDoc.data().createdAt?.toDate(),
          updatedAt: customerDoc.data().updatedAt?.toDate()
        } as Customer;
        
        setCustomerForInvitation(freshCustomer);
      } else {
        setCustomerForInvitation(customer);
      }
    } catch (error) {
      console.error('Error refreshing customer:', error);
      setCustomerForInvitation(customer);
    }
    
    setIsSendInvitationOpen(true);
  };

  // Szűrés kezelése
  const filteredCustomers = useMemo(() => {
    return customers.filter(customer => {
      // Név vagy email alapján szűrés
      const searchMatch =
        (customer.type === 'organization' ? customer.companyName : `${customer.lastName} ${customer.firstName}`)
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        // Adószám és cégjegyzékszám keresés
        customer.taxNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.registrationNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        // Cím elemek keresése
        customer.address?.street?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.address?.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.address?.postalCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.address?.country?.toLowerCase().includes(searchTerm.toLowerCase());

      // Szervezet alapján szűrés
      const orgMatch = selectedOrgs.length === 0 ||
        customer.organizations?.some(org => selectedOrgs.includes(org));

      // Ügyfélcsoport alapján szűrés (bármelyik kijelölt csoport tagja)
      const groupMatch = selectedGroupIds.length === 0 ||
        customerGroups.some(g => selectedGroupIds.includes(g.id) && (g.customerIds || []).includes(customer.id));

      return searchMatch && orgMatch && groupMatch;
    });
  }, [customers, searchTerm, selectedOrgs, selectedGroupIds, customerGroups]);

  // Pagination logic
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleAddOrganization = async (customer: Customer, orgId: string) => {
    try {
      const currentOrgs = customer.organizations || [];
      const newOrgs = [...currentOrgs, orgId];

      await updateCustomer(customer.id, {
        ...customer,
        organizations: newOrgs
      });
      toast.success(t('customers.messages.organizationsUpdated'));
    } catch (error) {
      console.error('Error adding organization:', error);
      toast.error(t('customers.messages.organizationsUpdateFailed'));
    }
  };

  // Csoportos szervezethez rendelés a kijelölt ügyfeleken
  const handleBulkAssignOrganization = async () => {
    if (!bulkOrgId || selectedCustomerIds.length === 0) return;
    setIsBulkAssigning(true);
    let done = 0;
    let skipped = 0;
    let failed = 0;
    try {
      for (const id of selectedCustomerIds) {
        const customer = customers.find(c => c.id === id);
        if (!customer) continue;
        const currentOrgs = customer.organizations || [];
        if (currentOrgs.includes(bulkOrgId)) { skipped++; continue; }
        try {
          await updateCustomer(customer.id, { ...customer, organizations: [...currentOrgs, bulkOrgId] });
          done++;
        } catch (e) {
          console.error('Bulk org assign failed for', id, e);
          failed++;
        }
      }
      const orgName = organizations.find(o => o.id === bulkOrgId)?.name || '';
      if (done > 0) toast.success(`${done} ügyfél hozzárendelve: ${orgName}${skipped ? ` (${skipped} már benne volt)` : ''}`);
      else if (skipped > 0) toast(`Mind a ${skipped} kijelölt ügyfél már ehhez a szervezethez tartozik`);
      if (failed > 0) toast.error(`${failed} ügyfélnél nem sikerült a hozzárendelés`);
      setSelectedCustomerIds([]);
      setBulkOrgId('');
    } finally {
      setIsBulkAssigning(false);
    }
  };

  const handleRemoveOrganization = async (customer: Customer, orgId: string) => {
    try {
      const currentOrgs = customer.organizations || [];
      const newOrgs = currentOrgs.filter(id => id !== orgId);

      await updateCustomer(customer.id, {
        ...customer,
        organizations: newOrgs
      });
      toast.success(t('customers.messages.organizationsUpdated'));
    } catch (error) {
      console.error('Error removing organization:', error);
      toast.error(t('customers.messages.organizationsUpdateFailed'));
    }
  };

  // Bulk select handlers
  const handleSelectCustomer = (customerId: string) => {
    setSelectedCustomerIds(prev =>
      prev.includes(customerId)
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCustomerIds.length === currentCustomers.length) {
      setSelectedCustomerIds([]);
    } else {
      setSelectedCustomerIds(currentCustomers.map(c => c.id));
    }
  };

  const handleClearSelection = () => {
    setSelectedCustomerIds([]);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-4 w-full min-w-0">
      <CommonPageHeader
        title={t('customers.title')}
        subtitle={t('customers.subtitle') || 'Ügyfelek kezelése és áttekintése'}
        icon={UsersIcon}
      >
        <div className="flex space-x-2">
          <button
            onClick={() => setIsRegistrationLinkOpen(true)}
            className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <LinkIcon className="h-4 w-4 mr-2" />
            <span>Regisztrációs link</span>
          </button>
          <button
            onClick={() => setIsImportWizardOpen(true)}
            className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
            <span>{tr.import.importButton.replace('{entity}', tr.import.customers)}</span>
          </button>
          <ExportButton
            data={filteredCustomers}
            mappings={customerFieldMappings}
            filename={`customers_${new Date().toISOString().split('T')[0]}`}
            label={tr.import.exportButton.replace('{entity}', tr.import.customers)}
          />
          <button
            onClick={() => { setSelectedCustomer(null); setIsModalOpen(true); }}
            className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            <span>{t('customers.new')}</span>
          </button>
        </div>
      </CommonPageHeader>

      {/* Szűrési lehetőségek - görgethetők mobilon */}
      <div className="space-y-2 sm:space-y-3">
        <div className="w-full">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t('common.placeholders.selectCustomer')}
          />
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto pb-2 scrollbar-none">
          <button
            onClick={() => setSelectedOrgs([])}
            className={`flex-none inline-flex items-center px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border text-xs sm:text-sm whitespace-nowrap
              ${selectedOrgs.length === 0
                ? 'border-primary-500 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'
              }`}
          >
            <BuildingOffice2Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            {t('customers.fields.organizations')}
          </button>
          {organizations.map((org) => (
            <button
              key={org.id}
              onClick={() => {
                setSelectedOrgs(prev =>
                  prev.includes(org.id)
                    ? prev.filter(id => id !== org.id)
                    : [...prev, org.id]
                );
              }}
              className={`flex-none inline-flex items-center px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border text-xs sm:text-sm whitespace-nowrap
                ${selectedOrgs.includes(org.id)
                  ? 'border-primary-500 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
            >
              <BuildingOffice2Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="max-w-[120px] sm:max-w-[150px] truncate">{org.name}</span>
            </button>
          ))}
        </div>

        {/* Ügyfélcsoport szűrő – csak ha van csoport */}
        {customerGroups.length > 0 && (
          <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto pb-2 scrollbar-none">
            <button
              onClick={() => setSelectedGroupIds([])}
              className={`flex-none inline-flex items-center px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border text-xs sm:text-sm whitespace-nowrap
                ${selectedGroupIds.length === 0
                  ? 'border-primary-500 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
            >
              <UsersIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Csoportok
            </button>
            {customerGroups.map((group) => (
              <button
                key={group.id}
                onClick={() => {
                  setSelectedGroupIds(prev =>
                    prev.includes(group.id) ? prev.filter(id => id !== group.id) : [...prev, group.id]
                  );
                  setCurrentPage(1);
                }}
                title={group.description || group.name}
                className={`flex-none inline-flex items-center px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border text-xs sm:text-sm whitespace-nowrap
                  ${selectedGroupIds.includes(group.id)
                    ? 'border-primary-500 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'
                  }`}
              >
                <span className="max-w-[120px] sm:max-w-[150px] truncate">{group.name}</span>
                <span className="ml-1.5 text-[11px] text-gray-400 tabular-nums">{group.customerIds?.length ?? group.customerCount ?? 0}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {/* Bulk Actions Toolbar */}
      {selectedCustomerIds.length > 0 && (
        <div className="mb-4 crm-card px-4 py-2.5 border-primary-200 dark:border-primary-500/30 bg-primary-50/60 dark:bg-primary-500/5">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-50 tabular-nums">
              {t('customers.bulkActions.selected', { count: selectedCustomerIds.length })}
            </span>
            <button onClick={() => setSelectedCustomerIds([])} className="text-sm text-primary-700 dark:text-primary-300 hover:underline">
              {t('customers.bulkActions.deselectAll')}
            </button>

            <div className="ml-auto flex flex-wrap items-center gap-2">
              {/* Szervezethez rendelés */}
              <div className="flex items-center gap-1.5">
                <select
                  value={bulkOrgId}
                  onChange={(e) => setBulkOrgId(e.target.value)}
                  className="h-9 text-sm border rounded-lg px-2.5 pr-8 min-w-[200px]"
                >
                  <option value="">Szervezethez rendelés…</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
                <button
                  onClick={handleBulkAssignOrganization}
                  disabled={!bulkOrgId || isBulkAssigning}
                  className="crm-btn crm-btn-primary h-9"
                >
                  {isBulkAssigning ? 'Folyamatban…' : 'Hozzárendel'}
                </button>
              </div>
              <button onClick={() => setShowGroupAssignment(true)} className="crm-btn crm-btn-secondary h-9">
                <UsersIcon className="h-4 w-4" />
                Csoporthoz
              </button>
              <button onClick={() => setIsConfirmBulkDeleteOpen(true)} className="crm-btn crm-btn-secondary h-9 text-red-700 dark:text-red-400">
                <TrashIcon className="h-4 w-4" />
                {t('customers.bulkActions.deleteSelected')}
              </button>
            </div>
          </div>
        </div>
      )}

      <CustomerList
        customers={currentCustomers}
        organizations={organizations}
        onStatusChange={handleStatusChange}
        onViewClick={openViewModal}
        onEditClick={openEditModal}
        onDeleteClick={(customer) => {
          setCustomerToDelete(customer);
          setIsConfirmDeleteOpen(true);
        }}
        onDocumentClick={openDocumentModal}
        onGenerateDocument={handleGenerateDocument}
        onAddOrganization={handleAddOrganization}
        onRemoveOrganization={handleRemoveOrganization}
        onSendInvitation={handleSendInvitation}
        selectedCustomerIds={selectedCustomerIds}
        onSelectCustomer={handleSelectCustomer}
        onSelectAll={handleSelectAll}
        showBulkSelect={true}
      />

      {/* Pagination controls */}
      <div className="relative z-10">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          totalItems={filteredCustomers.length}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          indexOfFirstItem={indexOfFirstItem}
          indexOfLastItem={indexOfLastItem}
        />
      </div>

      <CustomerViewModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        customer={selectedCustomer}
        organizations={organizations}
        onEdit={openEditModal}
      />

      <CustomerForm
        isOpen={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedCustomer(null);
        }}
        onSubmit={selectedCustomer ? handleEditCustomer : handleCreateCustomer}
        initialData={selectedCustomer}
      />

      {/* Document Viewer Modal */}
      <DocumentViewerModal
        isOpen={isDocumentModalOpen}
        onClose={() => setIsDocumentModalOpen(false)}
        customerWithDocuments={customerWithDocuments}
      />

      {/* Dokumentum generáló modal */}
      <DocumentGeneratorModal
        isOpen={isDocumentGeneratorOpen}
        onClose={() => setIsDocumentGeneratorOpen(false)}
        documentDataProvider={customerForDocGen}
      />

      {/* Confirm Delete Modal */}
      <ConfirmDeleteModal
        isOpen={isConfirmDeleteOpen}
        onClose={() => {
          setIsConfirmDeleteOpen(false);
          setCustomerToDelete(null);
        }}
        onConfirm={() => {
          if (customerToDelete) {
            handleDeleteCustomer(customerToDelete);
          }
          setIsConfirmDeleteOpen(false);
          setCustomerToDelete(null);
        }}
        title={t('customers.messages.confirmTitle')}
        deleteMessage={customerToDelete ?
          `${t('customers.messages.deleteQuestion')} "${customerToDelete.type === 'organization' ? customerToDelete.companyName : `${customerToDelete.firstName} ${customerToDelete.lastName}`}"?` :
          t('customers.messages.deleteQuestion')}
        deleteWarning={t('customers.messages.deleteWarning')}
      />

      {/* Confirm Bulk Delete Modal */}
      <ConfirmDeleteModal
        isOpen={isConfirmBulkDeleteOpen}
        onClose={() => setIsConfirmBulkDeleteOpen(false)}
        onConfirm={handleBulkDelete}
        title={t('customers.bulkActions.confirmDelete', { count: selectedCustomerIds.length })}
        deleteMessage={t('customers.bulkActions.confirmDeleteMessage')}
        deleteWarning={t('customers.messages.deleteWarning')}
      />

      {/* Customer Registration Link Generator Modal */}
      <CustomerRegistrationLinkGenerator
        isOpen={isRegistrationLinkOpen}
        onClose={() => setIsRegistrationLinkOpen(false)}
      />

      {/* Group Assignment Modal */}
      <GroupAssignmentModal
        isOpen={showGroupAssignment}
        onClose={() => setShowGroupAssignment(false)}
        customerIds={selectedCustomerIds}
        onSuccess={() => {
          setSelectedCustomerIds([]);
          setShowGroupAssignment(false);
        }}
      />

      {/* Send Invitation Modal */}
      {customerForInvitation && (
        <SendInvitationModal
          isOpen={isSendInvitationOpen}
          onClose={() => {
            setIsSendInvitationOpen(false);
            setCustomerForInvitation(null);
          }}
          customer={customerForInvitation}
          onSuccess={() => {
            // Refresh customer list to get updated contact persons
            refresh();
          }}
        />
      )}

      {/* Import Wizard */}
      <ImportWizard
        isOpen={isImportWizardOpen}
        onClose={() => setIsImportWizardOpen(false)}
        mappings={customerFieldMappings}
        entityName={tr.import.customers}
        templateFilename="customers_template"
        onImport={async (rows) => {
          const results = { success: 0, failed: 0, errors: [] as string[] };
          for (const row of rows) {
            try {
              const customerData: CreateCustomerData = {
                type: row.type || 'individual',
                companyName: row.companyName || '',
                firstName: row.firstName || '',
                lastName: row.lastName || '',
                email: row.email || '',
                phone: row.phone || '',
                taxNumber: row.taxNumber || '',
                organizations: [],
                notes: row.notes || '',
                isActive: row.isActive !== false,
                documents: [],
                address: {
                  street: row['address.street'] || '',
                  city: row['address.city'] || '',
                  postalCode: row['address.postalCode'] || '',
                  country: row['address.country'] || '',
                },
                contactPerson: {
                  name: row['contactPerson.name'] || '',
                  email: row['contactPerson.email'] || '',
                  phone: row['contactPerson.phone'] || '',
                  position: row['contactPerson.position'] || '',
                },
              };
              await createCustomer(customerData);
              results.success++;
            } catch (error) {
              results.failed++;
              results.errors.push(`Row: ${row.email || 'unknown'} - ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
          }
          if (results.success > 0) {
            refresh();
          }
          return results;
        }}
      />

    </div>
  );
};

export default Customers;